import { useState, useEffect } from 'react';
import { setUserData } from '@nsd/fe';
import { useSelector, useDispatch } from 'react-redux';
import { isEqual } from 'lodash';

function useDealers() {
  const dispatch = useDispatch();
  const { user, appSettings } = useSelector(state => state.account);
  const [activeDealer, setActiveDealer] = useState(null);

  /**
   * Change the active dealer to a new one and update the user's agents
   * to reflect the change.
   * @param {string} dealerCode 
   * @param {any[]} dealers list of currently loaded dealers
   */
  const selectDealer = (dealerCode, dealers) => {
    const newActiveAgent = dealers.find(agent => agent.dealerCode === dealerCode);
    let agents = dealers.map(agent => {
      if (agent.dealerCode == newActiveAgent?.dealerCode) {
        return {
          ...agent,
          active: true
        };
      } else {
        return {
          ...agent,
          active: false,
        };
      }
    });
    if (agents.length <= 1) {
      // Need to make sure agents is > 1 or the menu goes away....
      agents = [...agents, ...(user?.agents ?? []).map(c => ({ ...c, active: false }))];
    }
    setActiveDealer(newActiveAgent);
    dispatch(
      setUserData({ ...user, agents }, appSettings, user.roles, null)
    );
    localStorage.setItem('dealers', JSON.stringify(agents));
  }

  /**
   * @returns {object[]}
   */
  const getCachedDealers = () => {
    return JSON.parse(localStorage.getItem('dealers'));
  }

  /**
   * @param {object[]} dealers 
   */
  const setCachedDealers = (dealers) => {
    localStorage.setItem('dealers', JSON.stringify(dealers));
  }

  /**
   * @param {object[] | null} cachedDealers 
   * @param {object[]} newDealers 
   * @returns {boolean}
   */
  const areCachedDealersValid = (cachedDealers, newDealers) => {
    if (cachedDealers == null || !isEqual(cachedDealers, newDealers)) {
      return false;
    }

    const cachedDealerCodes = new Set(cachedDealers.map(dealer => dealer.dealerCode));
    for (const { dealerCode } of newDealers) {
      if (!cachedDealerCodes.has(dealerCode)) {
        return false;
      }
    }
    return true;
  }

  useEffect(() => {
    const userAgents = user?.agents ?? [];
    const newActiveDealer = userAgents.find(agent => agent.active);

    // Only update when the underlying dealer actually changes; this prevents
    // every user-state rebuild from producing a fresh activeDealer reference,
    // which would cause downstream effects (e.g., claim fetches) to refire.
    if (
      newActiveDealer &&
      newActiveDealer.dealerCode !== activeDealer?.dealerCode
    ) {
      setActiveDealer(newActiveDealer);
    }
  }, [user, activeDealer?.dealerCode]);

  return {
    activeDealer,
    selectDealer,
    getCachedDealers,
    areCachedDealersValid,
    setCachedDealers,
  };
}

export default useDealers;
